function normaliseText(value) {
  return String(value ?? "").trim();
}

function escapeRegExp(value) {
  return String(value).replace(
    /[.*+?^${}()|[\]\\]/g,
    "\\$&"
  );
}

function parseStructuredSections(questionText) {
  const contextSections = [
    ...questionText.matchAll(
      /<section\b([^>]*data-exam-context(?:\s*=\s*["'][^"']*["'])?[^>]*)>[\s\S]*?<\/section>/gi
    ),
  ]
    // A shared stimulus must never contain an assessable section. Some
    // model responses wrap a placeholder subquestion in a context section;
    // rendering both would show the same learner question twice.
    .filter(
      (match) =>
        !/\bdata-subquestion\s*=/i.test(match[0])
    )
    .map((match) => ({
      scope: normaliseText(
        match[1].match(
          /\bdata-context-for\s*=\s*["']([^"']+)["']/i
        )?.[1]
      ),
      html: match[0],
    }));
  const sections = [];
  const sectionPattern =
    /<section\b([^>]*\bdata-subquestion\s*=\s*["']([^"']+)["'][^>]*)>([\s\S]*?)<\/section>/gi;

  let match;

  while (
    (match = sectionPattern.exec(questionText)) !==
    null
  ) {
    const marksMatch = match[1].match(
      /\bdata-marks\s*=\s*["'](\d+)["']/i
    );
    const marks = Number(marksMatch?.[1]);

    if (!Number.isInteger(marks) || marks <= 0) {
      continue;
    }

    const number = normaliseText(match[2]);
    const sharedContext = contextSections
      .filter(
        (context) =>
          !context.scope ||
          number === context.scope ||
          number.startsWith(
            `${context.scope}.`
          )
      )
      .map((context) => context.html)
      .join("<br/>");

    sections.push({
      number,
      marks,
      questionText: [
        sharedContext,
        `<section>${normaliseText(match[3])}</section>`,
      ]
        .filter(Boolean)
        .join("<br/><br/>"),
    });
  }

  return sections;
}

function parseFallbackSections(
  parentQuestionNumber,
  questionText
) {
  const labelPattern = new RegExp(
    `\\b${escapeRegExp(parentQuestionNumber)}\\.\\d+(?:\\.\\d+)?\\b`,
    "g"
  );
  const matches = [
    ...questionText.matchAll(labelPattern),
  ];

  if (matches.length === 0) {
    return [];
  }

  const sharedContext = normaliseText(
    questionText.slice(0, matches[0].index)
  );
  const sections = [];
  let pendingContext = "";

  matches.forEach((match, index) => {
    const nextMatch = matches[index + 1];
    const block = normaliseText(
      questionText.slice(
        match.index,
        nextMatch?.index ?? questionText.length
      )
    );
    const markMatches = [
      ...block.matchAll(
        /(?:\((\d+)\)|\[(\d+)\])/g
      ),
    ];
    const finalMark =
      markMatches[markMatches.length - 1];
    const marks = Number(
      finalMark?.[1] || finalMark?.[2]
    );

    if (!Number.isInteger(marks) || marks <= 0) {
      pendingContext = [pendingContext, block]
        .filter(Boolean)
        .join("<br/>");
      return;
    }

    sections.push({
      number: normaliseText(match[0]),
      marks,
      questionText: [
        sharedContext,
        pendingContext,
        block,
      ]
        .filter(Boolean)
        .join("<br/><br/>"),
    });

    pendingContext = "";
  });

  return sections;
}

export function getExamSubquestions(question) {
  if (!question) {
    return [];
  }

  const parentQuestionNumber = normaliseText(
    question.question_number
  );
  const questionText = normaliseText(
    question.question_text
  );

  if (!parentQuestionNumber || !questionText) {
    return [];
  }

  const structured =
    parseStructuredSections(questionText);

  return structured.length > 0
    ? structured
    : parseFallbackSections(
      parentQuestionNumber,
      questionText
    );
}

export function getPresentedAIQuestion(session) {
  const question = session?.question;

  if (!question) {
    return null;
  }

  const isExam =
    session.session_state?.selection_type ===
      "EXAM" ||
    session.session_state?.mode ===
      "EXAM_SIMULATION";

  if (!isExam) {
    return question;
  }

  const subquestions =
    getExamSubquestions(question);
  const requestedNumber =
    session.session_state
      ?.current_subquestion_number ||
    subquestions[0]?.number;
  const subquestion = subquestions.find(
    (item) => item.number === requestedNumber
  );

  if (!subquestion) {
    return question;
  }

  return {
    ...question,
    parent_question_number:
      question.question_number,
    question_number:
      subquestion.number,
    question_text:
      subquestion.questionText,
    marks: subquestion.marks,
  };
}
